/**
 * CR-5563: KPI reporting periods for the supplier Performance tab.
 *
 * The dashboard's `getQuickRangeDates` covers the dashboard's own presets
 * (this month / last quarter / last year …). This ticket asks for a different
 * set — rolling windows plus Year to Date and All Time — so the options live
 * here, built on the dashboard's `formatLocalDate` so the date strings are
 * produced the same way (local, never a UTC-shifted toISOString).
 */
import { formatLocalDate } from '../../components/dashboard/dashboardFilterUtils';

export type PerformancePeriod =
  | 'last30Days'
  | 'last3Months'
  | 'last6Months'
  | 'last12Months'
  | 'yearToDate'
  | 'allTime';

export const DEFAULT_PERFORMANCE_PERIOD: PerformancePeriod = 'last12Months';

/**
 * The earliest date we send for "All Time".
 *
 * Omitting the dates entirely does NOT mean all-time: the backend's
 * `_apply_otif_window` falls back to a trailing 12 months whenever no dates are
 * supplied, and `_total_value_window` falls back to a trailing 365 days. That
 * is exactly why OTD alone reads "Last 12 months" today while OTIF and quality
 * read "All time". Sending a date far enough back makes every metric use one
 * genuinely unbounded window, so the three figures finally agree.
 */
export const ALL_TIME_FROM = '1900-01-01';

export interface PerformancePeriodOption {
  value: PerformancePeriod;
  label: string;
}

export const PERFORMANCE_PERIOD_OPTIONS: PerformancePeriodOption[] = [
  { value: 'last30Days', label: 'Last 30 Days' },
  { value: 'last3Months', label: 'Last 3 Months' },
  { value: 'last6Months', label: 'Last 6 Months' },
  { value: 'last12Months', label: 'Last 12 Months' },
  { value: 'yearToDate', label: 'Year to Date' },
  { value: 'allTime', label: 'All Time' },
];

export interface PerformanceWindow {
  dateFrom: string;
  dateTo: string;
}

/**
 * Resolve a period to the dates sent to `/suppliers/profiles/<id>/`.
 *
 * @param period The selected reporting period.
 * @param now    Injectable clock, so tests are not time-dependent.
 * @returns the inclusive date window, always both ends.
 */
export function resolvePerformanceWindow(
  period: PerformancePeriod,
  now: Date = new Date(),
): PerformanceWindow {
  const today = formatLocalDate(now);

  switch (period) {
    case 'last30Days': {
      const from = new Date(now);
      // 29 days back plus today = a 30-day window inclusive of both ends.
      from.setDate(from.getDate() - 29);
      return { dateFrom: formatLocalDate(from), dateTo: today };
    }
    case 'last3Months': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: formatLocalDate(from), dateTo: today };
    }
    case 'last6Months': {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 6);
      return { dateFrom: formatLocalDate(from), dateTo: today };
    }
    case 'yearToDate':
      return {
        dateFrom: formatLocalDate(new Date(now.getFullYear(), 0, 1)),
        dateTo: today,
      };
    case 'allTime':
      return { dateFrom: ALL_TIME_FROM, dateTo: today };
    case 'last12Months':
    default: {
      // A ROLLING twelve months ending today, not the dashboard's
      // getLast12MonthsRange(). That helper ends at the end of LAST month, so
      // the default period excluded everything from the 1st onwards — the
      // Daily OTIF chart would open up to 31 days stale and the live-arrears
      // bar could never render. Every other option here is a rolling window
      // ending today; this one now matches them.
      const from = new Date(now);
      from.setFullYear(from.getFullYear() - 1);
      return { dateFrom: formatLocalDate(from), dateTo: today };
    }
  }
}

/**
 * The period caption shown on the tab.
 *
 * Rendered from the selection rather than the serializer's
 * `*_timeframe_label`: the backend collapses ANY supplied window to the
 * literal string "Custom range", so "Last 30 Days" would be captioned
 * "Custom range" on all three cards.
 *
 * @param period The selected reporting period.
 * @param periodWindow Its resolved dates.
 * @returns e.g. "Last 12 Months (1 Oct 2025 – 30 Sep 2026)" or "All Time".
 */
export function describePerformancePeriod(
  period: PerformancePeriod,
  periodWindow: PerformanceWindow,
): string {
  const option = PERFORMANCE_PERIOD_OPTIONS.find(o => o.value === period);
  const name = option?.label ?? 'Custom';
  if (period === 'allTime') return name;

  // Parsed as LOCAL midnight. `new Date('2025-10-01')` is UTC midnight, which
  // renders as 30 Sep in any negative-offset locale — the same off-by-one the
  // chart's deleted toLocalISODate helper existed to prevent.
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(
      undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${name} (${fmt(periodWindow.dateFrom)} – ${fmt(periodWindow.dateTo)})`;
}

/**
 * Floor a window to the last calendar year, for the daily OTIF chart.
 *
 * The chart requests `period: 'day'` and the backend gap-fills every day in
 * range, so "All Time" (a 1900 start) would be ~46,000 points — a multi-MB
 * payload drawn as one SVG dot each. The KPI cards keep the full window; only
 * the daily series is floored.
 *
 * Anchored to a calendar year rather than a day count. A 365-day cap also
 * clamped the DEFAULT period, because "Last 12 Months" is `today − 1 year` =
 * 366 inclusive days (367 over a leap day), so every first load warned about
 * trimming a single day.
 *
 * @param periodWindow The selected period's dates.
 * @returns the dateFrom to request, and whether it was floored.
 */
export function clampToDailyChartWindow(
  periodWindow: PerformanceWindow,
): { dateFrom: string; clamped: boolean } {
  const from = new Date(`${periodWindow.dateFrom}T00:00:00`);
  const floor = new Date(`${periodWindow.dateTo}T00:00:00`);
  floor.setFullYear(floor.getFullYear() - 1);
  if (from < floor) return { dateFrom: formatLocalDate(floor), clamped: true };
  return { dateFrom: periodWindow.dateFrom, clamped: false };
}
